import { useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ChevronRight, MapPin, Search, X } from 'lucide-react'
import clsx from 'clsx'
import type { TestCompletionEndpointType } from '@/api/testCompletion'
import type {
  CourseNodeView,
  CurriculumNodeView,
  LearnerLibraryResponse,
  LibraryLessonNodeView,
  ModuleNodeView,
} from '@/types/coaching'
import {
  testCompletionEndpointKey,
  type TestCompletionEndpointOption,
} from './endpointOptions'

interface Props {
  tree: LearnerLibraryResponse['tree']
  type: TestCompletionEndpointType
  options: TestCompletionEndpointOption[]
  selectedEndpointKey: string
  onEndpointChange: (endpointKey: string) => void
}

interface CurrentPath {
  courseId: number
  moduleId: number
  lessonId: number
}

interface VisibleModule {
  node: ModuleNodeView
  lessons: LibraryLessonNodeView[]
}

interface VisibleCourse {
  node: CourseNodeView
  modules: VisibleModule[]
}

interface VisibleCurriculum {
  node: CurriculumNodeView
  courses: VisibleCourse[]
}

function findCurrentPath(tree: LearnerLibraryResponse['tree']): CurrentPath | null {
  let firstNotStarted: CurrentPath | null = null

  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        for (const lesson of module.lessons) {
          const path = { courseId: course.courseId, moduleId: module.moduleId, lessonId: lesson.lessonId }
          if (lesson.status === 'in_progress') return path
          if (firstNotStarted == null && lesson.status === 'not_started') firstNotStarted = path
        }
      }
    }
  }

  return firstNotStarted
}

function filterTree(
  tree: LearnerLibraryResponse['tree'],
  type: TestCompletionEndpointType,
  query: string,
): VisibleCurriculum[] {
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const matches = (...values: string[]) => (
    normalizedQuery.length === 0
    || values.some((value) => value.toLocaleLowerCase().includes(normalizedQuery))
  )

  return tree.flatMap((curriculum) => {
    const courses = curriculum.courses.flatMap((course) => {
      if (type === 'COURSE_END') {
        return matches(curriculum.title, course.title) ? [{ node: course, modules: [] }] : []
      }

      const modules = course.modules.flatMap((module) => {
        if (type === 'CHAPTER_END') {
          return matches(curriculum.title, course.title, module.title)
            ? [{ node: module, lessons: [] }]
            : []
        }

        const lessons = module.lessons.filter((lesson) => (
          matches(curriculum.title, course.title, module.title, lesson.title)
        ))
        return lessons.length > 0 ? [{ node: module, lessons }] : []
      })

      return modules.length > 0 ? [{ node: course, modules }] : []
    })

    return courses.length > 0 ? [{ node: curriculum, courses }] : []
  })
}

function completionLabel(completed: number, total: number, t: ReturnType<typeof useTranslation>['t']) {
  return t('testCompletion.endpoint.tree.progress', { completed, total })
}

export function TestCompletionEndpointTree({
  tree,
  type,
  options,
  selectedEndpointKey,
  onEndpointChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const currentPath = useMemo(() => findCurrentPath(tree), [tree])
  const [query, setQuery] = useState('')
  const [expandedCourseIds, setExpandedCourseIds] = useState<Set<number>>(
    () => new Set(currentPath ? [currentPath.courseId] : []),
  )
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<number>>(
    () => new Set(currentPath ? [currentPath.moduleId] : []),
  )
  const visibleTree = useMemo(() => filterTree(tree, type, query), [query, tree, type])
  const optionByKey = useMemo(
    () => new Map(options.map((option) => [testCompletionEndpointKey(option), option])),
    [options],
  )
  const selectedOption = optionByKey.get(selectedEndpointKey)
  const isSearching = query.trim().length > 0

  const toggle = (id: number, setter: Dispatch<SetStateAction<Set<number>>>) => {
    setter((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const endpointOption = (contentId: number) => optionByKey.get(`${type}:${contentId}`)

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60">
      <div className="border-b border-slate-200 bg-white p-3">
        <label className="sr-only" htmlFor={`test-completion-endpoint-search-${type}`}>
          {t('testCompletion.endpoint.tree.searchLabel')}
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            id={`test-completion-endpoint-search-${type}`}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('testCompletion.endpoint.tree.searchPlaceholder')}
            autoComplete="off"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-10 text-sm font-medium text-slate-900 outline-none transition focus-visible:border-cyan-500 focus-visible:ring-2 focus-visible:ring-cyan-100"
          />
          {query.length > 0 ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label={t('testCompletion.endpoint.tree.searchClear')}
              className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-600"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="max-h-96 space-y-3 overflow-y-auto overscroll-contain p-3">
        {visibleTree.map((curriculum) => (
          <section key={curriculum.node.curriculumId} className="[content-visibility:auto] [contain-intrinsic-size:auto_160px]">
            <div className="mb-2 flex items-center justify-between gap-3 px-1">
              <h4 className="truncate text-xs font-black uppercase tracking-[0.1em] text-slate-500">
                <ContentMetadataText text={curriculum.node.title} />
              </h4>
              <span className="shrink-0 text-[11px] font-bold text-slate-400">
                {completionLabel(curriculum.node.completedLessonCount, curriculum.node.lessonCount, t)}
              </span>
            </div>

            <div className="space-y-2">
              {curriculum.courses.map((course) => {
                const courseExpanded = isSearching || expandedCourseIds.has(course.node.courseId)
                const courseIsCurrent = currentPath?.courseId === course.node.courseId
                const courseOption = endpointOption(course.node.courseId)

                if (type === 'COURSE_END') {
                  return (
                    <EndpointRadio
                      key={course.node.courseId}
                      endpointKey={courseOption ? testCompletionEndpointKey(courseOption) : ''}
                      title={course.node.title}
                      meta={completionLabel(course.node.completedLessonCount, course.node.lessonCount, t)}
                      selected={courseOption != null && selectedEndpointKey === testCompletionEndpointKey(courseOption)}
                      completed={courseOption?.completed === true}
                      current={courseIsCurrent}
                      onSelect={onEndpointChange}
                    />
                  )
                }

                return (
                  <div key={course.node.courseId} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <button
                      type="button"
                      onClick={() => toggle(course.node.courseId, setExpandedCourseIds)}
                      aria-expanded={courseExpanded}
                      className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left hover:bg-cyan-50/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-600"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {courseExpanded
                          ? <ChevronDown className="size-4 shrink-0 text-cyan-700" aria-hidden="true" />
                          : <ChevronRight className="size-4 shrink-0 text-slate-400" aria-hidden="true" />}
                        <span className="truncate text-sm font-black text-slate-900"><ContentMetadataText text={course.node.title} /></span>
                        {courseIsCurrent ? <CurrentBadge label={t('testCompletion.endpoint.tree.current')} /> : null}
                      </span>
                      <span className="shrink-0 text-[11px] font-bold text-slate-500">
                        {completionLabel(course.node.completedLessonCount, course.node.lessonCount, t)}
                      </span>
                    </button>

                    {courseExpanded ? (
                      <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 p-2 pl-4">
                        {course.modules.map((module) => {
                          const moduleExpanded = isSearching || expandedModuleIds.has(module.node.moduleId)
                          const moduleIsCurrent = currentPath?.moduleId === module.node.moduleId
                          const moduleOption = endpointOption(module.node.moduleId)

                          if (type === 'CHAPTER_END') {
                            return (
                              <EndpointRadio
                                key={module.node.moduleId}
                                endpointKey={moduleOption ? testCompletionEndpointKey(moduleOption) : ''}
                                eyebrow={t('testCompletion.endpoint.tree.chapter', { number: module.node.chapterNumber })}
                                title={module.node.title}
                                meta={completionLabel(module.node.completedLessonCount, module.node.lessonCount, t)}
                                selected={moduleOption != null && selectedEndpointKey === testCompletionEndpointKey(moduleOption)}
                                completed={moduleOption?.completed === true}
                                current={moduleIsCurrent}
                                onSelect={onEndpointChange}
                              />
                            )
                          }

                          return (
                            <div key={module.node.moduleId} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                              <button
                                type="button"
                                onClick={() => toggle(module.node.moduleId, setExpandedModuleIds)}
                                aria-expanded={moduleExpanded}
                                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-cyan-50/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-600"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  {moduleExpanded
                                    ? <ChevronDown className="size-4 shrink-0 text-cyan-700" aria-hidden="true" />
                                    : <ChevronRight className="size-4 shrink-0 text-slate-400" aria-hidden="true" />}
                                  <span className="shrink-0 text-xs font-black text-cyan-800">
                                    {t('testCompletion.endpoint.tree.chapter', { number: module.node.chapterNumber })}
                                  </span>
                                  <span className="truncate text-sm font-bold text-slate-700"><ContentMetadataText text={module.node.title} /></span>
                                  {moduleIsCurrent ? <CurrentBadge label={t('testCompletion.endpoint.tree.current')} /> : null}
                                </span>
                                <span className="shrink-0 text-[11px] font-bold text-slate-400">
                                  {completionLabel(module.node.completedLessonCount, module.node.lessonCount, t)}
                                </span>
                              </button>

                              {moduleExpanded ? (
                                <div className="space-y-1 border-t border-slate-100 p-2 pl-5">
                                  {module.lessons.map((lesson) => {
                                    const lessonOption = endpointOption(lesson.lessonId)
                                    return (
                                      <EndpointRadio
                                        key={lesson.lessonId}
                                        endpointKey={lessonOption ? testCompletionEndpointKey(lessonOption) : ''}
                                        title={lesson.title}
                                        selected={lessonOption != null && selectedEndpointKey === testCompletionEndpointKey(lessonOption)}
                                        completed={lessonOption?.completed === true}
                                        current={currentPath?.lessonId === lesson.lessonId}
                                        onSelect={onEndpointChange}
                                        compact
                                      />
                                    )
                                  })}
                                </div>
                              ) : null}
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </section>
        ))}

        {visibleTree.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm font-semibold text-slate-500">
            {t('testCompletion.endpoint.tree.noSearchResults')}
          </p>
        ) : null}
      </div>

      <div className="border-t border-slate-200 bg-white px-4 py-3">
        {selectedOption ? (
          <div className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-cyan-700" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-cyan-700">
                {t('testCompletion.endpoint.tree.selected')}
              </p>
              <p className="mt-0.5 break-words font-bold text-slate-900">{selectedOption.path.join(' › ')}</p>
            </div>
          </div>
        ) : (
          <p className="text-xs font-semibold text-slate-500">
            {t('testCompletion.endpoint.tree.selectionRequired')}
          </p>
        )}
      </div>
    </div>
  )
}

function CurrentBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-cyan-100 px-2 py-0.5 text-[10px] font-black text-cyan-800">
      <MapPin className="size-3" aria-hidden="true" />
      {label}
    </span>
  )
}

function EndpointRadio({
  endpointKey,
  eyebrow,
  title,
  meta,
  selected,
  completed,
  current,
  compact = false,
  onSelect,
}: {
  endpointKey: string
  eyebrow?: string
  title: string
  meta?: string
  selected: boolean
  completed: boolean
  current: boolean
  compact?: boolean
  onSelect: (endpointKey: string) => void
}) {
  const { t } = useTranslation('coaching')
  const disabled = completed || endpointKey.length === 0

  return (
    <label className={clsx(
      'group flex items-center gap-3 rounded-lg border text-left transition-colors',
      compact ? 'px-3 py-2' : 'px-3 py-3',
      disabled
        ? 'cursor-not-allowed border-transparent bg-slate-100/70 text-slate-400'
        : 'cursor-pointer border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/60',
      selected ? 'border-cyan-600 bg-cyan-50 ring-1 ring-cyan-600' : null,
    )}>
      <input
        type="radio"
        name="test-completion-endpoint"
        value={endpointKey}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(endpointKey)}
        className="size-4 shrink-0 accent-cyan-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600"
      />
      <span className="min-w-0 flex-1">
        {eyebrow ? <span className="block text-[10px] font-black uppercase tracking-[0.1em] text-cyan-700">{eyebrow}</span> : null}
        <span className={clsx('block truncate text-sm font-bold', disabled ? 'text-slate-400' : 'text-slate-800')}>
          <ContentMetadataText text={title} />
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {current ? <CurrentBadge label={t('testCompletion.endpoint.tree.current')} /> : null}
        {completed ? (
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-black text-slate-500">
            {t('testCompletion.endpoint.completed')}
          </span>
        ) : meta ? (
          <span className="text-[11px] font-bold text-slate-400">{meta}</span>
        ) : null}
      </span>
    </label>
  )
}
